/* eslint-disable */
// @ts-nocheck

import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { faArrowLeft, faTruck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'

import OrderStatusBadge from '@/components/misc/badges/OrderStatusBadge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Container } from '@/interfaces/container'
import { formatDateForTableSeparatedDateAndTime } from '@/utils/helpers'

import { useLogisticContainer } from '../hooks/useLogisticContainer'
import { useServerLogisticContainer } from '../hooks/useServerLogisticContainer'
import { useTableContext } from './TableContext'

export const columns: ColumnDef<Container>[] = [
  {
    accessorKey: 'operationNumber',
    header: 'N° de operación',
    cell: ({ row }) => (
      <p className={'font-medium'}>{row.original.operationNumber || '-'}</p>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    cell: ({ row }) => <span>{row.original.name || '-'}</span>,
    enableHiding: false,
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
    enableHiding: false,
  },
  {
    accessorKey: 'categories',
    header: 'Categorías',
    cell: ({ row }) => (
      <span>
        {Array.isArray(row.original.categories)
          ? row.original.categories.map((cat) => cat.name).join(', ')
          : '-'}
      </span>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'cost',
    header: 'Costo',
    cell: ({ row }) => (
      <span>{row.original.cost != null ? `$${row.original.cost}` : '-'}</span>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'percentProductsSales',
    header: '% Vendido',
    cell: ({ row }) => {
      const percent = row.original.percentProductsSales ?? 0
      return (
        <div className="flex min-w-[100px] items-center gap-2">
          <div className="h-3 w-full overflow-hidden rounded bg-gray-200">
            <div
              className="h-3 rounded bg-green-500"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <span className="ml-2 text-xs font-medium">{percent}%</span>
        </div>
      )
    },
    enableHiding: false,
  },
  {
    accessorKey: 'totalItems',
    header: 'Total de elementos',
    cell: ({ row }) => <span>{row.original.totalItems || 0}</span>,
    enableHiding: true,
  },
  {
    accessorKey: 'createdAt',
    header: 'Creado el',
    cell: ({ row }) => {
      const { date, time } = formatDateForTableSeparatedDateAndTime(
        row.original?.createdAt
      )
      return (
        <div className="flex flex-col items-start">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'ownerCompany',
    header: 'Proveedor',
    cell: ({ row }) => <span>{row.original.ownerCompany?.name || '-'}</span>,
    enableHiding: true, // Ahora es ocultable
    // defaultHidden: true, // Oculta por defecto (manejada en DataTable)
  },
  {
    accessorKey: 'managerCompany',
    header: 'Manejador',
    cell: ({ row }) => <span>{row.original.managerCompany?.name || '-'}</span>,
    enableHiding: true, // Ahora es ocultable
    // defaultHidden: true, // Oculta por defecto (manejada en DataTable)
  },
  {
    accessorKey: 'importerCompany',
    header: 'Importadora',
    cell: ({ row }) => <span>{row.original.importerCompany?.name || '-'}</span>,
    enableHiding: true,
  },
  {
    accessorKey: 'transporterCompany',
    header: 'Transportista',
    cell: ({ row }) => (
      <span>{row.original.transporterCompany?.name || '-'}</span>
    ),
    enableHiding: true,
  },
  {
    accessorKey: 'storageCompany',
    header: 'Almacenaje',
    cell: ({ row }) => <span>{row.original.storageCompany?.name || '-'}</span>,
    enableHiding: true,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Última actualización',
    cell: ({ row }) => {
      const { date, time } = formatDateForTableSeparatedDateAndTime(
        row.original?.updatedAt
      )
      return (
        <div className="flex flex-col items-start">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    id: 'identificativo',
    header: '', // Sin nombre de columna
    cell: ({ row }) => {
      const isGreen =
        row.original.isReturned || row.original.status === 'REFUNDED'
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="relative flex cursor-help items-center justify-center">
                <FontAwesomeIcon
                  className="h-5 w-5 text-black"
                  icon={faTruck}
                />
                <FontAwesomeIcon
                  className={`absolute bottom-0 right-0 h-3 w-3 ${isGreen ? 'text-green-600' : 'text-red-700'}`}
                  icon={faArrowLeft}
                  style={{ transform: 'translate(30%, 30%)' }}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>¿Es retornado?</p>
              <p className="text-xs text-muted-foreground">
                {isGreen ? 'Sí, ha sido retornado' : 'No, no ha sido retornado'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
    enableHiding: false,
  },
  {
    id: 'actions',
    header: 'Acciones',
    cell: ({ row }) => {
      const { onRowClick } = useLogisticContainer()
      const { refetch } = useTableContext()
      const [showConfirm, setShowConfirm] = useState(false)
      const { deleteContainer } = useServerLogisticContainer()
      const deleteMutation = deleteContainer(row.original.id)
      return (
        <div className={'flex justify-start'}>
          <div className={`p-2`} role={'button'}>
            <PencilSquareIcon
              className={`h-5 w-5 text-blue-700`}
              onClick={(e) => {
                e.stopPropagation()
                onRowClick(row.original)
              }}
            />
          </div>
          <div className={`p-2`} role={'button'}>
            <TrashIcon
              className={`h-5 w-5 text-red-700`}
              onClick={(e) => {
                e.stopPropagation()
                setShowConfirm(true)
              }}
            />
            <AlertDialog open={showConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar contenedor?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción eliminará permanentemente el contenedor. ¿Desea
                    continuar?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setShowConfirm(false)}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      deleteMutation.mutate(undefined, {
                        onSuccess: () => {
                          toast.success('Contenedor eliminado')
                          setShowConfirm(false)
                          refetch && refetch()
                        },
                        onError: () => {
                          toast.error('Error al eliminar contenedor')
                          setShowConfirm(false)
                        },
                      })
                    }}
                  >
                    Aceptar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )
    },
    enableHiding: false,
  },
]
